// PHASE_1 / B0 — HR detail panel composite (ADR-015 frontend kit; [D6]).
//
// The single canonical detail surface every P1+ resource screen reuses to
// show one record's fields beside the list (the list/detail master-detail
// pattern the gap §10.1 dual-role brief mandates). Composes the existing
// Drawer primitive (which composes Modal — focus-trap, Escape, scrim-click
// all inherited) + a titled header, a scrollable body slot, and an optional
// footer for actions (Edit / Approve / Reject — those buttons are caller-
// rendered and gated by <ScopeGate> for UX convenience; the server is the
// authority per ADR-017).
//
// It owns no fetching and no field layout: the host passes already-localized
// title + the body content (typically a <dl> of label/value rows where money
// renders through <Money> and dates/strings are i18n keys at the call site).

import { forwardRef, type ReactNode } from "react";

import { cx } from "../lib/cx.ts";
import { Drawer, type DrawerSide } from "../Drawer/Drawer.tsx";

export interface DetailDrawerProps {
  /** Whether the drawer is open. */
  readonly open: boolean;
  /** Close handler — fires on Escape, scrim-click, and the close control. */
  readonly onClose: () => void;
  /** Already-localized panel title (host passes t("...")). */
  readonly title: string;
  /** Already-localized accessible label for the close control. */
  readonly closeLabel: string;
  /** Which edge the drawer slides from — defaults to right. */
  readonly side?: DrawerSide;
  /** Optional footer (actions) rendered below the body — typically buttons. */
  readonly footer?: ReactNode;
  /** The detail content (field rows). */
  readonly children?: ReactNode;
  readonly className?: string;
}

/**
 * A titled detail panel anchored to a page edge. Behaviorally a Drawer (modal
 * with edge layout); the header binds the title for `aria-labelledby` so screen
 * readers announce the panel. The body scrolls independently of the header +
 * footer so long records stay navigable.
 */
export const DetailDrawer = forwardRef<HTMLDivElement, DetailDrawerProps>(
  ({ open, onClose, title, closeLabel, side = "right", footer, children, className }, ref) => {
    const titleId = "detail-drawer-title";
    return (
      <Drawer
        ref={ref}
        open={open}
        onClose={onClose}
        side={side}
        aria-labelledby={titleId}
        data-component="detail-drawer"
        className={cx("nai-detail-drawer", className)}
      >
        <header data-slot="detail-drawer-header" className="nai-detail-drawer-header">
          <h2 id={titleId} className="nai-detail-drawer-title">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={closeLabel}
            className="nai-detail-drawer-close"
            data-slot="detail-drawer-close"
          >
            <span aria-hidden="true">×</span>
          </button>
        </header>

        <div data-slot="detail-drawer-body" className="nai-detail-drawer-body">
          {children}
        </div>

        {footer !== undefined && (
          <footer data-slot="detail-drawer-footer" className="nai-detail-drawer-footer">
            {footer}
          </footer>
        )}
      </Drawer>
    );
  },
);
DetailDrawer.displayName = "DetailDrawer";
